<template>
  <div>
    <slot name="mobile-text-slider"/>

    <div v-if="!isLoading && rows.length === 0" class="onls-table__empty">
      <div class="onls-table__empty-icon">📭</div>

      <slot name="empty-table" />
    </div>

    <div v-else class="onls-table__container">
      <table class="onls-table">
        <thead>
        <tr class="onls-table__head-th">
          <th v-for="(col, c) in columns" class="onls-table__th" :key="c" :style="{width: col.width || 'auto' }">
            <div class="d-flex ai-center">
              <div v-if="col.sortable" class="onls-table__sort" @click="onSort(col.field)">
                <chevron-icon
                    :class="['onls-table__sort-up', { 'onls-table__sort--show': !(table.sortable.sort === 'asc' && table.sortable.order === col.field) }]"
                />

                <chevron-icon
                    :class="['onls-table__sort-down', { 'onls-table__sort--show': !(table.sortable.sort === 'desc' && table.sortable.order === col.field) }]"
                />
              </div>

              <div>
                {{ col.label || '' }}
              </div>
            </div>
          </th>
        </tr>
        </thead>
        <draggable v-model="modelRows" tag="tbody" handle=".onls-table__td-drag">
          <template #item="{element, index}">
            <tr class="onls-table__tr">
              <td v-for="(item, i) in columns" :class="['onls-table__td', { 'onls-table__td-drag': item.field === 'order' }]" :key="i">
                <div v-if="item.field === 'order'" class="onls-table__td-drag-content">
                  <hamburger-icon />

                  <span>{{ index + 1 }}</span>
                </div>

                <slot v-else-if="item.field === 'actions'" name="actions" :item="element"></slot>

                <slot v-else-if="$slots[item.field]" :name="item.field" :item="element"/>

                <span class="color-primary" v-else>
              {{ getColValue(element[item.field]) }}
            </span>
              </td>
            </tr>
          </template>
        </draggable>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
import draggable from "../draggable/vuedraggable";
import {computed, PropType, reactive} from "vue";
import type { TableColumn } from "./table.type";
import ChevronIcon from "../icons/chevron.vue";
import HamburgerIcon from "../icons/hamburger.vue";

const props = defineProps({
  columns: { type: Array as PropType<Array<TableColumn>>, required: true },
  rows: { type: Array as PropType<Array<Record<string, any>>>, required: true },
  count: { type: Number, default: 0 },
  isLoading: { type: Boolean, default: false },
});

const emits = defineEmits(['update:rows']);

const table = reactive({
  sortable: {
    order: "name",
    sort: "",
  },
});

const onSort = (order: string) => {
  const nextSort = table.sortable.sort === '' ? 'asc' : table.sortable.sort === 'desc' ? '' : 'desc';

  table.sortable = {
    sort: nextSort,
    order,
  }
};

const getColValue = (value: any) => {
  if (value === null || value === undefined) {
    return '';
  }

  return value;
}

const modelRows = computed({
  get() {
    return props.rows;
  },
  set(value) {
    emits('update:rows', value);
  }
})
</script>

<style lang="scss">
.onls-table {
  border-collapse: separate;
  border-spacing: 0 10px;
  width: 100%;
  margin-bottom: -10px;

  &__empty {
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 5px;
    background: var(--secondary-background);
    padding: 30px 0;
    text-align: center;

    &-icon {
      font-size: 40px;
      margin-right: 50px;
    }

    &-title {
      font-size: 20px;
      color: var(--primary-text);
      text-transform: uppercase;
    }

    &-desc {
      margin-top: 15px;
      font-size: 15px;
      text-transform: uppercase;
      color: var(--primary-text);
    }
  }

  &__container {
    width: 100%;
    overflow-y: hidden;
    overflow-x: auto;
    scrollbar-base-color: var(--primary-text);
    //margin-bottom: -10px;

    &::-webkit-scrollbar {
      background: var(--primary-background);
      height: 5px;
    }

    &::-webkit-scrollbar-thumb {
      background: var(--primary-text);
    }
  }

  &__sort {
    display: flex;
    flex-direction: column;
    margin-right: 8px;
    path {
      fill: var(--primary-text);
    }

    &-up,
    &-down {
      cursor: pointer;
      opacity: 0;
    }

    &-down {
      margin-top: 3px;
      transform: rotate(180deg);
    }

    &--show {
      opacity: 1;
    }
  }

  &__th {
    background: transparent;
    color: var(--primary-text);
    font-weight: 400;
    font-size: 16px;
    text-transform: uppercase;
    border: none;
    padding: 0 20px;
    text-align: start;
  }

  &__td {
    border: none;
    padding: 30px 20px;
    background: var(--secondary-background);
    font-size: 16px;
    font-weight: 400;
    text-transform: uppercase;
    color: var(--primary-text);

    &-drag {
      //padding: 0;
      cursor: pointer;

      &-content {
        height: 100%;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: start;
        background: var(--secondary-background);

        span {
          margin-left: 40px;
          font-size: 16px;
          font-weight: 400;
          text-transform: uppercase;
          color: var(--primary-text);
        }
      }
    }
  }

  &__tr {
    td:first-child {
      border-top-left-radius: 5px;
      border-bottom-left-radius: 5px;
    }

    td:last-child {
      border-top-right-radius: 5px;
      border-bottom-right-radius: 5px;
    }
  }

  @media screen and (max-width: 768px) {
    &__th {
      font-size: 12px;
    }

    &__td {
      padding: 15px 20px;
      font-size: 12px;
    }
  }
}
</style>
